// The hour a festival "day" turns over. A set time before it belongs to the
// previous night running past midnight: it sorts after 23:59 and really happens
// on `day + 1`. Derived per event from its sets (`deriveDayCutoff`) and stored
// on the event as `dayCutoffHour`; read it through `eventDayCutoff()`.

/** Fallback for events with no derived cutoff (no set times, or not backfilled yet). */
export const DEFAULT_DAY_CUTOFF_HOUR = 10

export function eventDayCutoff(event: { dayCutoffHour?: number } | null | undefined): number {
  const h = event?.dayCutoffHour
  return typeof h === 'number' && h >= 0 && h < 24 ? h : DEFAULT_DAY_CUTOFF_HOUR
}

export function hhmmToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

/** Minutes since the start of the set's festival day — post-cutoff hours roll past 24h. */
export function festivalMinutes(time: string, cutoff: number): number {
  const min = hhmmToMinutes(time)
  return min < cutoff * 60 ? min + 1440 : min
}

/** Sort key across days: the set's day, then its festival-day minutes. */
export function setSortKey(set: { day: string; startTime?: string }, cutoff: number): string {
  const min = set.startTime ? festivalMinutes(set.startTime, cutoff) : 0
  return `${set.day} ${String(min).padStart(4, '0')}`
}

interface TimedSet {
  day: string
  startTime?: string
  endTime?: string
}

const BUCKET_MIN = 15
const BUCKETS = 1440 / BUCKET_MIN
const MAX_SET_MIN = 16 * 60

function dayIndex(day: string): number {
  return Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10)) / 60_000
}

function setSpan(s: TimedSet): [number, number] {
  const a = hhmmToMinutes(s.startTime!)
  const b = s.endTime ? hhmmToMinutes(s.endTime) : a + 60
  return [a, b <= a ? b + 1440 : b]
}

// "A little after the last set ends": the start of the longest daily stretch
// with nothing playing, plus up to 2h. A daytime event (nothing crosses
// midnight and the day starts after 06:00) keeps calendar days.
function targetHour(sets: TimedSet[]): number {
  const busy = new Array<boolean>(BUCKETS).fill(false)
  for (const s of sets) {
    const [a, b] = setSpan(s)
    for (let i = Math.floor(a / BUCKET_MIN); i < Math.ceil(b / BUCKET_MIN); i++) busy[i % BUCKETS] = true
  }
  let best = { len: 0, start: 0 }
  for (let i = 0; i < BUCKETS; i++) {
    if (busy[i] || !busy[(i - 1 + BUCKETS) % BUCKETS]) continue
    let len = 0
    while (len < BUCKETS && !busy[(i + len) % BUCKETS]) len++
    if (len > best.len) best = { len, start: i }
  }
  if (best.len === 0) return DEFAULT_DAY_CUTOFF_HOUR
  const a = best.start * BUCKET_MIN
  const b = a + best.len * BUCKET_MIN
  if (b >= 1440 && b % 1440 >= 6 * 60) return 0
  return Math.round((a + Math.min(120, (b - a) / 2)) / 60) % 24
}

// How badly cutoff `c` fits the stored day labels; null when it contradicts
// them. Lower is better: first no "day" that starts entirely after midnight,
// then the smallest hole inside any day.
function misfit(sets: TimedSet[], c: number, startDate?: string): [number, number] | null {
  const byDay = new Map<string, [number, number][]>()
  for (const s of sets) {
    const [a, b] = setSpan(s)
    const shift = a < c * 60 ? 1440 : 0
    const start = dayIndex(s.day) + a + shift
    const end = start + (b - a)
    if (end - start > MAX_SET_MIN) return null
    const list = byDay.get(s.day) ?? []
    list.push([start, end])
    byDay.set(s.day, list)
  }
  const days = [...byDay.keys()].sort()
  // Days keep their order: nothing on one day starts after the next day's
  // first set. Ends may run over — another stage can still be playing.
  for (let i = 0; i + 1 < days.length; i++) {
    const lastStart = Math.max(...byDay.get(days[i])!.map(x => x[0]))
    const nextFirst = Math.min(...byDay.get(days[i + 1])!.map(x => x[0]))
    if (lastStart > nextFirst) return null
  }
  // An evening start date explains a day whose only sets are past midnight
  // (a lone 00:15 set labelled with the party's night).
  const eveningStart = (day: string) =>
    !!startDate && startDate.slice(0, 10) === day && Number(startDate.slice(11, 13) || 0) >= 12
  let shiftedDays = 0
  let worstHole = 0
  for (const [day, spans] of byDay) {
    spans.sort((x, y) => x[0] - y[0])
    if (spans[0][0] >= dayIndex(day) + 1440 && !eveningStart(day)) shiftedDays++
    let reach = spans[0][1]
    for (const [start, end] of spans) {
      worstHole = Math.max(worstHole, start - reach)
      reach = Math.max(reach, end)
    }
  }
  return [shiftedDays, worstHole]
}

/**
 * The event's day cutoff hour (0–23) implied by its sets and their `day`
 * labels, or null when it has no timed sets. Only the sets decide it — event
 * start/end dates are too often off to constrain it; `startDate` only explains
 * a night whose sets all fall after midnight.
 */
export function deriveDayCutoff(allSets: readonly TimedSet[], startDate?: string): number | null {
  const sets = allSets.filter(s => !!s.startTime && /^\d{2}:\d{2}/.test(s.startTime) && !!s.day)
  if (sets.length === 0) return null
  const target = targetHour(sets)
  let best: { c: number; shifted: number; hole: number; dist: number } | null = null
  for (let c = 0; c < 24; c++) {
    const fit = misfit(sets, c, startDate)
    if (!fit) continue
    const [shifted, hole] = fit
    const dist = Math.min(Math.abs(c - target), 24 - Math.abs(c - target))
    // Holes within an hour of each other are a tie, decided by the target.
    const better =
      !best ||
      shifted < best.shifted ||
      (shifted === best.shifted && (hole < best.hole - 59 || (Math.abs(hole - best.hole) <= 59 && dist < best.dist)))
    if (better) best = { c, shifted, hole, dist }
  }
  return best?.c ?? null
}
